/* eslint-disable ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { service } from '@ember/service';
import config from 'linced-admin/config/environment';

export default class SchemaUpdateController extends Controller {
  @action
  setDialogOpen(value) {
    this.dialogOpen = value;
  }

  @action
  setMode(value) {
    this.mode = value;
  }

  @service
  fetch;

  @tracked
  selectedSchools = null;
  @tracked
  mode = 'all';
  @tracked
  bulkUpdateInProgress = false;
  @tracked
  dialogOpen = false;
  @tracked schoolsToUpdate = 0;
  @tracked schoolsUpdated = 0;
  @tracked schoolsFailed = 0;

  @computed('schoolsUpdated', 'schoolsFailed')
  get schoolsProcessed() {
    return this.schoolsUpdated + this.schoolsFailed;
  }

  get progress() {
    if (this.schoolsToUpdate <= 0) {
      return 0;
    }
    return (this.schoolsProcessed / this.schoolsToUpdate) * 100;
  }

  // bulkUpdateChunk posts a single chunk of schools. Renamed from `bulkUpdate`
  // when the actions hash was flattened: the `bulkUpdate` action below is now a
  // sibling class member and would otherwise shadow this helper.
  bulkUpdateChunk(schoolIds) {
    let url = `${config.APP.apiHost}/finance/v4/schools/:bulkupdateschema`;
    let data = {};
    if (!isEmpty(schoolIds)) {
      data.schoolIds = [...schoolIds];
    }

    return this.fetch
      .post(url, {
        dataType: 'json',
        data: JSON.stringify(data),
      })
      .then(() => {
        this.incrementProperty('schoolsUpdated', schoolIds.length);
      })
      .catch(() => {
        this.incrementProperty('schoolsFailed', schoolIds.length);
      });
  }

  @action
  toggleSchool(school) {
    if (isEmpty(this.selectedSchools)) {
      this.selectedSchools = [];
    }
    if (this.selectedSchools.includes(school.id)) {
      this.selectedSchools = this.selectedSchools.filter(
        (id) => id !== school.id,
      );
    } else {
      this.selectedSchools = [...this.selectedSchools, school.id];
    }
  }

  @action
  bulkUpdate() {
    const CHUNK_SIZE = 25;

    // Determine schools to be updated.
    let schoolIds = this.selectedSchools;
    if (this.mode === 'all') {
      schoolIds = this.model.schools
        .map((school) => school.id)
        .filter((id) => id != null);
    }

    this.bulkUpdateInProgress = true;
    this.dialogOpen = true;
    this.schoolsToUpdate = schoolIds.length;
    this.schoolsUpdated = 0;
    this.schoolsFailed = 0;

    // Break up schools into chunks.
    let chunks = [];
    for (let i = 0; i < schoolIds.length; i += CHUNK_SIZE) {
      let slice = schoolIds.slice(i, i + CHUNK_SIZE);
      chunks.push(slice);
    }

    chunks
      .reduce((chain, chunk) => {
        return chain.then(() => {
          return this.bulkUpdateChunk(chunk);
        });
      }, Promise.resolve())
      .finally(() => {
        this.bulkUpdateInProgress = false;
      });
  }

  reset() {
    this.selectedSchools = null;
    this.mode = 'all';
    this.bulkUpdateInProgress = false;
  }
}
